package de.connect2x.timmy.brandings.lifetimeTheme

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.compositeOver
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.mikepenz.aboutlibraries.ui.compose.LibraryDefaults
import com.mikepenz.aboutlibraries.ui.compose.m3.chipColors
import com.mikepenz.aboutlibraries.ui.compose.m3.libraryColors
import de.connect2x.messenger.compose.view.theme.*
import de.connect2x.messenger.compose.view.theme.components.*
import de.connect2x.messenger.compose.view.theme.components.ProgressIndicatorStyle.CircularProgressIndicatorStyle
import de.connect2x.messenger.compose.view.theme.components.ProgressIndicatorStyle.LinearProgressIndicatorStyle

class ThemeComponentsLifetimeImpl : ThemeComponents {
    @Composable
    private fun focusedBorder(color: Color): BorderStroke? =
        if (IsFocusHighlighting.current) {
            BorderStroke(
                width = MaterialTheme.messengerFocusIndicator.borderWidth,
                color = color,
            )
        } else null

    @OptIn(ExperimentalMaterial3Api::class, ExperimentalThemingApi::class)
    @Composable
    // This configuration tries to be as faithful as possible to our old design.
    // Even in places where our old design has low contrast or uneven spacing.
    override fun create(): ComponentStyles = ComponentStyles(
        // system
        systemUi = SystemUiStyle.default(
            header = MaterialTheme.colorScheme.surfaceColorAtElevation(8.dp),
            footer = MaterialTheme.colorScheme.background,
        ),
        // buttons
        primaryButton = ButtonStyle.filled(
            colors = ButtonDefaults.buttonColors(
                containerColor = lt_light_blue,
                contentColor = lt_blue,
            ),
            textStyle = MaterialTheme.typography.labelLarge,
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onPrimaryContainer),
        ),
        secondaryButton = ButtonStyle.filled(
            colors = ButtonDefaults.buttonColors(
                containerColor = MaterialTheme.colorScheme.secondaryContainer,
                contentColor = MaterialTheme.colorScheme.onSecondaryContainer,
            ),
            textStyle = MaterialTheme.typography.labelLarge,
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onSecondaryContainer),
        ),
        commonButton = ButtonStyle.outlined(
            colors = ButtonDefaults.buttonColors(
                containerColor = Color.White,
                contentColor = lt_blue,
            ),
            textStyle = MaterialTheme.typography.labelLarge,
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onPrimaryContainer),
        ),
        destructiveButton = ButtonStyle.outlined(
            colors = ButtonDefaults.outlinedButtonColors(
                containerColor = MaterialTheme.colorScheme.error,
                contentColor = Color.White,
            ),
            enabledBorder = BorderStroke(1.dp, MaterialTheme.colorScheme.error),
            textStyle = MaterialTheme.typography.labelLarge,
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onError),
        ),
        primaryIconButton = IconButtonStyle.filled(
            colors = IconButtonDefaults.filledIconToggleButtonColors(
                containerColor = lt_light_blue,
                contentColor = lt_blue,
            ),
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onPrimaryContainer),
        ),
        //Change for avatar icon button with camera
        secondaryIconButton = IconButtonStyle.filled(
            colors = IconButtonDefaults.filledIconToggleButtonColors(
                containerColor = lt_light_blue,
                contentColor = lt_blue,
            ),
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onSecondaryContainer),
        ),
        commonIconButton = IconButtonStyle.default(
            colors = IconButtonDefaults.iconToggleButtonColors(
                contentColor = Color.LocalContent,
            ),
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onSurface),
        ),
        destructiveIconButton = IconButtonStyle.default(
            colors = IconButtonDefaults.iconToggleButtonColors(
                contentColor = MaterialTheme.colorScheme.error,
            ),
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onErrorContainer),
        ),
        floatingActionButton = FloatingActionButtonStyle.default(
            size = 40.dp,
            containerColor = lt_light_blue,
            contentColor = lt_blue,
            elevation = FloatingActionButtonDefaults.elevation(0.dp, 0.dp, 0.dp, 0.dp),
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onPrimaryContainer),
        ),
        floatingActionButtonDisabled = FloatingActionButtonStyle.default(
            size = 40.dp,
            containerColor = lt_light_gray,
            elevation = FloatingActionButtonDefaults.elevation(0.dp, 0.dp, 0.dp, 0.dp),
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onPrimaryContainer),
        ),
        reactionButton = ButtonStyle.outlined(
            iconSize = 18.dp,
            iconSpacing = 4.dp,
            contentPadding = PaddingValues(12.dp, 4.dp),
            colors = ButtonDefaults.outlinedButtonColors(
                contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
            ),
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onPrimaryContainer),
        ),
        // this is the one that the user selected
        selectedReactionButton = ButtonStyle.filledTonal(
            iconSize = 18.dp,
            iconSpacing = 4.dp,
            contentPadding = PaddingValues(12.dp, 4.dp),
            colors = ButtonDefaults.filledTonalButtonColors(
                containerColor = MaterialTheme.colorScheme.secondary,
                contentColor = MaterialTheme.colorScheme.onSecondary,
                disabledContainerColor = MaterialTheme.colorScheme.secondaryContainer,
                disabledContentColor = MaterialTheme.colorScheme.onSecondaryContainer,
            ),
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onPrimaryContainer),
        ),
        // other inputs
        listItem = ListItemStyle.default(),
        checkbox = CheckboxStyle.default(),
        radioButton = RadioButtonStyle.default(),
        switch = SwitchStyle.default(
            colors = SwitchDefaults.colors(
                uncheckedThumbColor = lt_blue,
                uncheckedTrackColor = Color.White,
                uncheckedBorderColor = lt_blue,
                checkedThumbColor = Color.White,
                checkedBorderColor = lt_blue,
                checkedTrackColor = lt_blue,
            )
        ),
        // surfaces
        background = SurfaceStyle.default(
            color = MaterialTheme.colorScheme.surfaceContainer,
            contentColor = MaterialTheme.colorScheme.onSurface,
        ),
        popup = SurfaceStyle.default(
            shadowElevation = 4.dp,
            tonalElevation = 4.dp,
            shape = MaterialTheme.shapes.medium
        ),
        sidebar = SurfaceStyle.default(
            color = if (CurrentThemeSettings.isDarkMode()) MaterialTheme.colorScheme.primaryContainer else lt_light_blue,
            contentColor = MaterialTheme.colorScheme.onSurface,
        ),
        details = SurfaceStyle.default(
            color = MaterialTheme.colorScheme.onPrimary,
            tonalElevation = 0.dp,
        ),
        header = SurfaceStyle.default(
            color = lt_blue,
            contentColor = Color.White,
        ),
        timeline = SurfaceStyle.default(
            color = MaterialTheme.colorScheme.onPrimary,
            contentColor = MaterialTheme.colorScheme.onSurface,
        ),
        label = SurfaceStyle.default(
            color = MaterialTheme.colorScheme.onSecondary.copy(alpha = 0.25f)
                .compositeOver(MaterialTheme.colorScheme.surface),
            contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
            shape = RoundedCornerShape(16.dp),
            border = BorderStroke(width = 1.dp, color = MaterialTheme.colorScheme.outline),
            contentPadding = PaddingValues(horizontal = 6.dp, vertical = 3.dp),
            textStyle = MaterialTheme.typography.bodySmall,
        ),
        errorBanner = SurfaceStyle.default(
            color = MaterialTheme.colorScheme.errorContainer,
            contentColor = MaterialTheme.colorScheme.onErrorContainer,
        ),
        warningBanner = SurfaceStyle.default(
            color = MaterialTheme.messengerColors.warning,
            contentColor = MaterialTheme.colorScheme.onSurface,
        ),
        commonBanner = SurfaceStyle.default(
            color = MaterialTheme.colorScheme.surface,
            contentColor = MaterialTheme.colorScheme.onSurface,
        ),
        //dialogs
        dialog = SurfaceStyle.default(
            shape = RoundedCornerShape(4.dp),
            color = MaterialTheme.colorScheme.background,
            contentColor = MaterialTheme.colorScheme.onBackground,
        ),
        adaptiveDialog = DialogStyle.adaptiveDialog(),
        modalDialog = DialogStyle.modalDialog(),
        // dividers
        horizontalDivider = DividerStyle.default(),
        verticalDivider = DividerStyle.default(),
        // room list
        roomListElement = SurfaceStyle.default(
            shape = RoundedCornerShape(8.dp),
            color = if (CurrentThemeSettings.isDarkMode()) lt_dark_gray else Color.White,
            contentColor = MaterialTheme.colorScheme.onSurface,
            padding = PaddingValues(12.dp, 6.dp),
            shadowElevation = 2.dp,
        ),
        roomListSelection = SurfaceStyle.default(
            shape = RoundedCornerShape(8.dp),
            color = lt_blue,
            contentColor = Color.White,
            padding = PaddingValues(12.dp, 6.dp),
            shadowElevation = 4.dp,
        ),
        roomListDivider = null,
        accountSelector = ButtonStyle.filled(
            contentPadding = PaddingValues(0.dp),
            colors = ButtonDefaults.buttonColors(
                contentColor = Color.LocalContent,
                containerColor = Color.Transparent,
                disabledContentColor = Color.LocalContent,
                disabledContainerColor = Color.Transparent,
            ),
            elevation = null,
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onSurface),
        ),
        // input area
        inputAreaSurface = SurfaceStyle.default(
            color = MaterialTheme.colorScheme.onPrimary,
            tonalElevation = 0.dp,
            shadowElevation = 4.dp
        ),
        inputArea = InputAreaStyle.default(
            colors = TextFieldDefaults.colors(
                cursorColor = lt_dark_gray,

                focusedContainerColor = lt_light_blue,
                unfocusedContainerColor = lt_light_blue,
                disabledContainerColor = lt_light_blue,
                errorContainerColor = lt_light_blue,

                focusedIndicatorColor =
                    if (IsFocusHighlighting.current) MaterialTheme.colorScheme.primary
                    else Color.Transparent,
                unfocusedIndicatorColor =
                    if (IsFocusHighlighting.current) MaterialTheme.colorScheme.onSurfaceVariant
                    else Color.Transparent,
                disabledIndicatorColor =
                    if (IsFocusHighlighting.current) MaterialTheme.colorScheme.onSurface.copy(alpha = 0.38f)
                    else Color.Transparent,
                errorIndicatorColor =
                    if (IsFocusHighlighting.current) MaterialTheme.colorScheme.error
                    else Color.Transparent,

                unfocusedPlaceholderColor = lt_dark_gray.copy(alpha = 0.4f),
                focusedPlaceholderColor = lt_dark_gray.copy(alpha = 0.4f),
                disabledPlaceholderColor = lt_dark_gray.copy(alpha = 0.4f),
                errorPlaceholderColor = lt_dark_gray.copy(alpha = 0.4f),

                unfocusedTextColor = lt_dark_gray,
                focusedTextColor = lt_dark_gray,
                disabledTextColor = lt_dark_gray,
                errorTextColor = lt_dark_gray,
            ),
        ),
        // file viewer
        fileViewerSurface = SurfaceStyle.default(
            color = Color.Black,
            contentColor = lt_light_gray,
        ),
        fileViewerIconButton = IconButtonStyle.filled(
            colors = IconButtonDefaults.iconToggleButtonColors(
                containerColor = lt_dark_gray,
                contentColor = lt_light_gray,
            ),
        ),
        // message bubbles
        messageBubbleOwn = SurfaceStyle.default(
            color = lt_light_blue,
            contentColor = lt_dark_gray,
            shape = RoundedCornerShape(8.dp),
        ),
        messageBubbleOther = SurfaceStyle.default(
            color = lt_light_gray,
            contentColor = lt_dark_gray,
            shape = RoundedCornerShape(8.dp),
        ),
        messageBubbleError = SurfaceStyle.default(
            color = MaterialTheme.colorScheme.surface,
            contentColor = MaterialTheme.colorScheme.error,
            shape = RoundedCornerShape(8.dp),
        ),
        messageReference = SurfaceStyle.default(
            shape = RoundedCornerShape(8.dp),
            color = lt_light_gray,
        ),
        // tooltip
        tooltip = TooltipStyle.default(
            contentPadding = PaddingValues(5.dp),
            colors = TooltipDefaults.richTooltipColors(
                contentColor = if (CurrentThemeSettings.isDarkMode()) Color.White else lt_dark_gray,
                containerColor = if (CurrentThemeSettings.isDarkMode()) lt_dark_gray else Color.White,
            ),
            textStyle = MaterialTheme.typography.bodySmall,
        ),
        // loading spinner
        circularProgressIndicator = CircularProgressIndicatorStyle.default(),
        smallCircularProgressIndicator = CircularProgressIndicatorStyle.default(
            size = 32.dp,
        ),
        extraSmallCircularProgressIndicator = CircularProgressIndicatorStyle.default(
            size = 24.dp,
            strokeWidth = 2.dp,
        ),
        switchProgressIndicator = CircularProgressIndicatorStyle.default(
            size = 24.dp,
            strokeWidth = 2.dp,
        ),
        linearProgressIndicator = LinearProgressIndicatorStyle.default(),
        // slider
        slider = SliderStyle.default(
            colors = SliderDefaults.colors(
                thumbColor = lt_blue,
                activeTrackColor = lt_blue,
                activeTickColor = lt_light_blue,
                inactiveTrackColor = lt_light_blue,
                inactiveTickColor = lt_blue,
            ),
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onSurfaceVariant)
        ),
        // avatar
        avatar = AvatarStyle.default(
            color = MaterialTheme.colorScheme.primaryContainer,
            contentColor = MaterialTheme.colorScheme.onPrimaryContainer,
            outerBorder = BorderStroke(2.dp, MaterialTheme.colorScheme.secondaryContainer),
            innerBorder = BorderStroke(1.dp, MaterialTheme.colorScheme.surface),
            shape = CircleShape,
        ),
        // chips
        primaryChip = ChipStyle.default(
            colors = ChipStyle.Colors.default(
                containerColor = MaterialTheme.colorScheme.surfaceContainerLow,
                labelColor = lt_blue,
                disabledContainerColor = Color.White,
                selectedContainerColor = lt_blue,
                selectedLeadingIconColor = Color.White,
                selectedTrailingIconColor = Color.White,
            ),
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onPrimaryContainer),
        ),
        secondaryChip = ChipStyle.default(
            colors = ChipStyle.Colors.default(
                containerColor = MaterialTheme.colorScheme.surfaceContainerLow,
                disabledContainerColor = MaterialTheme.colorScheme.surfaceContainerLow,
                selectedContainerColor = MaterialTheme.colorScheme.secondaryContainer,
                selectedLabelColor = MaterialTheme.colorScheme.onSecondaryContainer,
                selectedLeadingIconColor = MaterialTheme.colorScheme.onSecondaryContainer,
                selectedTrailingIconColor = MaterialTheme.colorScheme.onSecondaryContainer,
            ),
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onPrimaryContainer),
        ),
        commonChip = ChipStyle.default(
            colors = ChipStyle.Colors.default(
                containerColor = MaterialTheme.colorScheme.surfaceContainerLow,
                disabledContainerColor = MaterialTheme.colorScheme.surfaceContainerLow,
            ),
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onSurface),
        ),
        destructiveChip = ChipStyle.default(
            colors = ChipStyle.Colors.default(
                containerColor = MaterialTheme.colorScheme.surfaceContainerLow,
                disabledContainerColor = MaterialTheme.colorScheme.surfaceContainerLow,
                selectedContainerColor = MaterialTheme.colorScheme.errorContainer,
                selectedLabelColor = MaterialTheme.colorScheme.onErrorContainer,
                selectedLeadingIconColor = MaterialTheme.colorScheme.onErrorContainer,
                selectedTrailingIconColor = MaterialTheme.colorScheme.onErrorContainer,
            ),
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onPrimaryContainer),
        ),
        mentionChip = ChipStyle.default(
            colors = ChipStyle.Colors.default(
                containerColor = MaterialTheme.colorScheme.surfaceContainerLow,
                disabledContainerColor = MaterialTheme.colorScheme.surfaceContainerLow,
            ),
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onSurface),
        ),
        // dropdown
        dropdownMenu = SurfaceStyle.default(
            color = MenuDefaults.containerColor,
            tonalElevation = MenuDefaults.TonalElevation,
            shadowElevation = MenuDefaults.ShadowElevation,
            shape = MenuDefaults.shape,
            border = focusedBorder(MaterialTheme.colorScheme.onSurface),
        ),
        dropdownMenuItem = DropdownMenuItemStyle.default(
            contentPadding = PaddingValues(horizontal = 10.dp),
            focusedBorder = focusedBorder(MaterialTheme.colorScheme.onSurface),
        ),
        // select, like HTML <select>
        select = SelectStyle.default(),
        // selection
        selectionOnSurface = SelectionStyle.onSurface(),
        selectionOnPrimary = SelectionStyle.onPrimary(),
        // other
        settingsItem = ListItemStyle.default(
            colors = ListItemDefaults.colors(
                containerColor = Color.Transparent,
            )
        ),
        library = LibraryStyle.default(
            colors = LibraryDefaults.libraryColors(
                libraryBackgroundColor = Color.Transparent,
                licenseChipColors = LibraryDefaults.chipColors(),
            ),
            textStyles = LibraryDefaults.libraryTextStyles(
                defaultOverflow = TextOverflow.Ellipsis,
                nameMaxLines = 10,
                versionMaxLines = 2
            ),
        ),
    )
}

